/**
 * URL-derived request metadata for the global API client.
 *
 * Resolves the caller's language, client version, and originating URL purely
 * from `window.location` and the static deployment config — no Datastore or
 * React state involved. The language chain checks, in order: login-redirect
 * fragment, then the `language_g` query param, then the domain map, then the
 * path silo's configured default, then the deployment default. The silo key
 * comes from the shared `getSiloKeyFromWindowUrl()`, which strips any
 * deployment `basePath` prefix — don't re-parse the path here. Runs on every
 * outgoing API call via the RPCLink `headers` callback, so the value always
 * reflects the current URL.
 *
 * `useSiloLanguage` exposes the same resolution as a hook for components
 * that render per-request language directly rather than through the API
 * client.
 */
import { coerceLegacyOrIsoLanguage } from '@repo/shared/legacy-language-adapter';
import type { IsoLocale } from '@repo/shared/locale';
import type { RequestMetadata } from '@repo/shared/orpc/metadata-headers';
import Constants from 'expo-constants';
import { getLanguageFromLoginRedirectFragment, getSiloKeyFromWindowUrl } from 'platform-specific/url';
import { domainToLanguageMap } from 'psi.config';
import { getGlobalParams } from 'util/navigate';
import { getDeploymentConfig, getDomain } from 'util/util';

export function resolveLanguageFromUrl(): IsoLocale {
    const deploymentConfig = getDeploymentConfig();
    const siloKey = getSiloKeyFromWindowUrl();
    const loginRedirectLanguage = getLanguageFromLoginRedirectFragment();
    const urlLanguage = getGlobalParams().language;
    const domainLanguage = domainToLanguageMap[getDomain()];
    const siloConfigLanguage = siloKey ? deploymentConfig?.siloConfig?.[siloKey]?.defaultLanguage : undefined;
    const serverLanguage = deploymentConfig?.defaultLanguage;
    const resolved = loginRedirectLanguage || urlLanguage || domainLanguage || siloConfigLanguage || serverLanguage;
    return coerceLegacyOrIsoLanguage(resolved);
}

export function useSiloLanguage(): IsoLocale {
    return resolveLanguageFromUrl();
}

export function resolveRequestMetadataFromUrl(): RequestMetadata {
    return {
        language: resolveLanguageFromUrl(),
        clientVersion: Constants.expoConfig?.version || undefined,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
    };
}
